import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { IOverlayLoaderStore } from '../../../../../modules/common/OverlayLoader.store';
import { MarkingToolAddedEventType } from '../../../../../modules/projects/details/annotationTools/markingTools/add/events/MarkingToolAddedEvent';
import { MarkingToolEditedEventType } from '../../../../../modules/projects/details/annotationTools/markingTools/edit/events/MarkingToolEditedEvent';
import { ACDLError, ForbiddenError, InputStatus } from '../../../../models/error.model';
import { EventListeningDisposer, IEventBus } from '../../../../services/eventBus.service';
import { NotificationLevel, ToastNotification } from '../../../notifications/models/notification.model';
import { INotificationsService } from '../../../notifications/services/notifications.service';
import { ProjectStatus } from '../../../projects/projects.model';
import { ICanPublishProjectResponse, ToolDeleteStrategy } from '../../projectDetails.models';
import { IProjectDetailsSubmodule } from '../../services/IProjectDetailsSubmodule';
import { IAnnotationType, IQuestion } from './projectDetailsTools.models';
import { IProjectDetailsToolsStore } from './projectDetailsTools.store';
import { IAnnotationTypesApiService } from './services/annotationTypesApi.service';
import { IQuestionsApiService } from './services/questionsApi.service';

export interface IProjectDetailsToolsBl extends IProjectDetailsSubmodule {
  addAnnotationTypeAsync(annotationType: IAnnotationType): Promise<void>;
  updateAnnotationTypeAsync(annotationType: IAnnotationType): Promise<void>;
  isUniqueAnnotationTypeNameAsync(toolName: string, id: string): Promise<boolean>;
  deleteAnnotationTypeAsync(id: string, strategy: ToolDeleteStrategy): Promise<void>;
  changeAnnotationTypeOrderAsync(annotationTypeId: string, index: number): Promise<void>;
  getAnnotationTypeUsageAsync(annotationTypeId: string): Promise<void>;
  getAnnotationTypeAffectAsync(annotationTypeId: string): Promise<void>;
  addQuestionAsync(question: IQuestion, id?: string): Promise<void>;
  updateQuestionAsync(question: IQuestion, strategy: ToolDeleteStrategy): Promise<void>;
  changeQuestionOrderAsync(questionId: string, index: number, isAttribute: boolean): Promise<void>;
  deleteQuestionAsync(id: string, strategy: ToolDeleteStrategy): Promise<void>;
  getQuestionAnswersAndScopesUsageAsync(questionId: string, answerIds: string[], scopesIds: string[]): Promise<void>;
  getQuestionAnswersAndScopesAffectAsync(questionId: string, answerIds: string[], scopesIds: string[]): Promise<void>;
  getQuestionUsageAsync(questionId: string): Promise<void>;
  getQuestionAffectAsync(questionId: string): Promise<void>;
  isUniqueQuestionTextAsync(text: string, id: string, isAttribute: boolean): Promise<boolean>;
  getProjectDetailsAnnotationViewAsync(projectId: string): Promise<void>;
  store: IProjectDetailsToolsStore;
}

export namespace IProjectDetailsToolsBl {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsToolsBl> = Symbol('PROJECT_DETAILS_TOOLS_SERVICE');
}

@injectable()
export class ProjectDetailsToolsBl implements IProjectDetailsToolsBl {
  private markingToolAddedDisposer?: EventListeningDisposer;
  private markingToolEditedDisposer?: EventListeningDisposer;

  constructor(
    @inject(IProjectDetailsToolsStore.$) public readonly store: IProjectDetailsToolsStore,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IQuestionsApiService.$) private readonly questionsApiService: IQuestionsApiService,
    @inject(IAnnotationTypesApiService.$) private readonly annotationTypesApiService: IAnnotationTypesApiService,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore,
    @inject(IEventBus.$) private readonly eventBus: IEventBus
  ) {
    makeObservable(this);
  }

  @action
  handleMarkingToolAddedAsync = async (): Promise<void> => {
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  };

  @action
  handleMarkingToolEditedAsync = async (): Promise<void> => {
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  };

  initialize(): void {
    this.store.resetStore();
    this.markingToolAddedDisposer = this.eventBus.addListener(this.handleMarkingToolAddedAsync, MarkingToolAddedEventType);
    this.markingToolEditedDisposer = this.eventBus.addListener(this.handleMarkingToolEditedAsync, MarkingToolEditedEventType);
  }

  cleanup(): void {
    this.store.resetStore();
    this.markingToolAddedDisposer?.();
    this.markingToolEditedDisposer?.();
  }

  async addAnnotationTypeAsync(annotationType: IAnnotationType): Promise<void> {
    const result = await this.annotationTypesApiService.addAnnotationTypeAsync({
      annotationTypeId: annotationType.id,
      color: annotationType.color,
      name: annotationType.name,
      order: annotationType.order,
      projectId: this.store.id,
      selectorType: annotationType.selectorType,
    });
    await this.notifyAboutApiError(result, 'adding_annotation_type_failed', true);
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async updateAnnotationTypeAsync(annotationType: IAnnotationType): Promise<void> {
    const result = await this.annotationTypesApiService.updateAnnotationTypeAsync({
      color: annotationType.color,
      name: annotationType.name,
      id: annotationType.id,
      selectorType: annotationType.selectorType,
      order: annotationType.order,
    });

    await this.notifyAboutApiError(result, 'updating_annotation_type_failed');
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async isUniqueAnnotationTypeNameAsync(name: string, annotationTypeId: string): Promise<boolean> {
    const result = await this.annotationTypesApiService.checkAnnotationTypeNameUniqueness({ annotationTypeId, name, projectId: this.store.id });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return true;
    }
    return result;
  }

  async getAnnotationTypeUsageAsync(annotationTypeId: string): Promise<void> {
    const result = await this.annotationTypesApiService.getAnnotationTypeUsage({ annotationTypeId });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setToolUsage(result);
  }

  async getAnnotationTypeAffectAsync(annotationTypeId: string): Promise<void> {
    const result = await this.annotationTypesApiService.getAnnotationTypeAffect({ annotationTypeId });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setToolAffect(result);
  }

  async deleteAnnotationTypeAsync(id: string, strategy: ToolDeleteStrategy): Promise<void> {
    const result = await this.annotationTypesApiService.deleteAnnotationTypeAsync({
      strategy,
      annotationTypeId: id,
      projectId: this.store.id,
    });

    if (result instanceof ACDLError) {
      if (result.isBadRequestWithCode(['NO_STRATEGY_SELECTED'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, 'delete_tool_select_strategy'));
      } else {
        await this.notifyAboutApiError(result, 'delete_annotation_type_failed');
      }
    }

    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async changeAnnotationTypeOrderAsync(annotationTypeId: string, index: number): Promise<void> {
    this.store.reorderAnnotationTypes(annotationTypeId, index);

    const result = await this.annotationTypesApiService.updateAnnotationTypesOrderAsync({
      projectId: this.store.id,
      toolOrders: this.store.annotationTypes.map((x) => ({ id: x.id, order: x.order })),
    });

    await this.notifyAboutApiError(result, 'updating_annotation_type_failed');
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async changeQuestionOrderAsync(questionId: string, index: number, isAttribute: boolean): Promise<void> {
    this.store.reorderQuestions(questionId, index, isAttribute);

    const result = await this.questionsApiService.updateQuestionsOrderAsync({
      projectId: this.store.id,
      orders: this.store.questions.map((x) => ({ id: x.id, order: x.order })),
    });

    await this.notifyAboutApiError(result, 'updating_question_failed', true);
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async addQuestionAsync(question: IQuestion, id?: string): Promise<void> {
    const result = await this.questionsApiService.addQuestionAsync({
      projectId: id || this.store.id,
      questionId: question.id,
      answerType: question.type,
      answers: question.answers,
      isRequired: question.isRequired,
      scopes: question.scopes,
      text: question.questionText,
    });

    await this.notifyAboutApiError(result, 'adding_question_failed', true);
    await this.getProjectDetailsAnnotationViewAsync(id || this.store.id);
    this.validate();
  }

  async updateQuestionAsync(question: IQuestion, strategy: ToolDeleteStrategy): Promise<void> {
    const result = await this.questionsApiService.updateQuestionAsync({
      strategy,
      projectId: this.store.id,
      questionType: question.type,
      questionId: question.id,
      answers: question.answers,
      isRequired: question.isRequired,
      scopes: question.scopes,
      text: question.questionText,
      order: question.order,
    });

    await this.notifyAboutApiError(result, 'updating_question_failed', true);
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async deleteQuestionAsync(questionId: string, strategy: ToolDeleteStrategy): Promise<void> {
    const question = this.store.questions.find((q) => q.id === questionId);
    const result = await this.questionsApiService.deleteQuestionAsync({ strategy, questionId, projectId: this.store.id });

    if (result instanceof ACDLError) {
      if (result.isBadRequestWithCode(['NO_STRATEGY_SELECTED'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, 'delete_tool_select_strategy'));
      } else {
        await this.notifyAboutApiError(result, question && question.scopes.includes('Image') ? 'delete_question_failed' : 'delete_attribute_failed');
      }
    }
    await this.getProjectDetailsAnnotationViewAsync(this.store.id);
    this.validate();
  }

  async getQuestionUsageAsync(questionId: string): Promise<void> {
    const result = await this.questionsApiService.getQuestionUsage({ questionId });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setToolUsage(result);
  }

  async getQuestionAffectAsync(questionId: string): Promise<void> {
    const result = await this.questionsApiService.getQuestionAffect({ questionId });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setToolAffect(result);
  }

  async getQuestionAnswersAndScopesUsageAsync(questionId: string, answerIds: string[], scopesIds: string[]): Promise<void> {
    const result = await this.questionsApiService.getQuestionAnswersUsage({ questionId, answerIds, scopesIds });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setToolUsage(result);
  }

  async getQuestionAnswersAndScopesAffectAsync(questionId: string, answerIds: string[], scopesIds: string[]): Promise<void> {
    const result = await this.questionsApiService.getQuestionAnswersAffect({ questionId, answerIds, scopesIds });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setToolAffect(result);
  }

  async getProjectDetailsAnnotationViewAsync(projectId: string): Promise<void> {
    this.overlayLoaderStore.enableLoader('getProjectDetailsAnnotation');
    const result = await this.questionsApiService.getProjectDetailsAnnotationViewAsync({ projectId });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    this.overlayLoaderStore.disableLoader('getProjectDetailsAnnotation');
    if (result instanceof ACDLError) {
      return;
    }

    this.store.setRetrievedData(projectId, result.status, result.annotationTypes, result.questions);
  }

  async isUniqueQuestionTextAsync(text: string, questionId: string, isAttribute: boolean): Promise<boolean> {
    const result = await this.questionsApiService.checkQuestionTextUniqueness({ text, questionId, isAttribute, projectId: this.store.id });
    await this.notifyAboutApiError(result, 'get_annotation_settings_failed');
    if (result instanceof ACDLError) {
      return true;
    }
    return result;
  }

  handleCanPublishCheck(response: ICanPublishProjectResponse): void {
    this.store.setValidationStatus(response.areToolsValid ? InputStatus.valid() : InputStatus.buildFrom(['no_segmentations_or_questions_added']));
  }

  validate(): void {
    if (this.store.status === ProjectStatus.Published) {
      return;
    }
    this.store.setValidationStatus(
      this.store.annotationTypes.length > 0 || this.store.questions.length > 0
        ? InputStatus.valid()
        : InputStatus.buildFrom(['no_segmentations_or_questions_added'])
    );
  }

  getValidationErrors(): string[] {
    return this.store.toolsStatus.errorCodes;
  }

  private async notifyAboutApiError(result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  }
}
